import { Badge } from "@/components/ui/badge";

import type {
  EchoflicksSessionMedia,
  ImageTriageDto,
  ImageTriageGroup as ImageTriageGroupDto,
} from "../../types";
import {
  exactMediaByKey,
  groupAdjudication,
  groupCategory,
  groupObservations,
} from "./imageTriageUtils";
import { ImageTriageThumb } from "./ImageTriageThumb";

export function ImageTriageGroup({
  group,
  dto,
  media,
  number,
  onOpenGallery,
}: {
  group: ImageTriageGroupDto;
  dto: ImageTriageDto;
  media: EchoflicksSessionMedia[];
  number: number;
  onOpenGallery: (items: EchoflicksSessionMedia[], index: number) => void;
}) {
  const observations = groupObservations(group, dto);
  const mediaByKey = exactMediaByKey(media);
  const galleryItems = observations
    .map((observation) => mediaByKey.get(observation.source.source_key))
    .filter((item): item is EchoflicksSessionMedia => Boolean(item));
  const heading = {
    duplicate: "Strict duplicate group",
    variant: "Altered or annotated variant",
    similar: "Similar moment",
    proposed: "AI-proposed moment",
  }[groupCategory(group)];
  const adjudication = groupAdjudication(group, dto);
  const adjudicationBadge = adjudication
    ? {
        same_moment: "AI: same moment",
        different_moments: "AI: different moments",
        mixed: "AI: partly same moment",
      }[adjudication.verdict]
    : null;

  return (
    <section
      className="rounded-lg border p-3 sm:p-4"
      aria-labelledby={`image-triage-group-${group.group_id}`}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 id={`image-triage-group-${group.group_id}`} className="font-medium">
          {heading} {number} · {observations.length}{" "}
          {observations.length === 1 ? "photo" : "photos"}
        </h4>
        <div className="flex flex-wrap items-center gap-2">
          {adjudicationBadge ? (
            <Badge
              variant="outline"
              className="border-blue-300 text-blue-900 dark:text-blue-200"
              title={adjudication?.reason ?? undefined}
            >
              {adjudicationBadge}
            </Badge>
          ) : null}
          {!group.projection_complete ? (
            <Badge variant="outline">Still indexing</Badge>
          ) : group.confidence_band ? (
            <Badge variant="outline" className="capitalize">
              {group.confidence_band.replaceAll("_", " ")}
            </Badge>
          ) : null}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {observations.map((observation) => {
          const item = mediaByKey.get(observation.source.source_key);
          const galleryIndex = item
            ? galleryItems.findIndex((candidate) => candidate.id === item.id)
            : -1;
          return (
            <ImageTriageThumb
              key={observation.occurrence_id}
              observation={observation}
              media={item}
              isReference={group.display_anchor === observation.occurrence_id}
              onOpen={
                item && galleryIndex >= 0
                  ? () => onOpenGallery(galleryItems, galleryIndex)
                  : undefined
              }
            />
          );
        })}
      </div>
    </section>
  );
}
